import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BellIcon, XMarkIcon, CheckCircleIcon, ExclamationCircleIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { isExpired } from './FileBrowser';

export interface Notification {
  id: string;
  type: 'download' | 'upload' | 'error' | 'success' | 'info';
  title: string;
  message: string;
  progress?: number;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  timestamp: Date;
  canDismiss: boolean;
  onCancel?: () => void;
  /** Presigned download URL for completed batch ZIP jobs */
  downloadUrl?: string;
  /** ISO-8601 expiry timestamp for the ZIP presigned URL (optional — absent for legacy records) */
  urlExpiresAt?: string;
  /** Job ID for the batch ZIP download (used to regenerate the URL) */
  jobId?: string;
  /** Callback to regenerate an expired ZIP URL — set by FileBrowser, called from NotificationCenter */
  onRegenerate?: () => void;
}

interface NotificationCenterProps {
  notifications: Notification[];
  onDismiss: (id: string) => void;
  onClearAll: () => void;
}

export default function NotificationCenter({ notifications, onDismiss, onClearAll }: NotificationCenterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const activeCount = notifications.filter(n => 
    n.status === 'pending' || n.status === 'processing'
  ).length;

  const hasUnread = notifications.some(n => 
    n.status === 'completed' || n.status === 'failed'
  );

  const getStatusIcon = (notification: Notification) => {
    switch (notification.status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'failed':
      case 'cancelled':
        return <ExclamationCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ArrowDownTrayIcon className="h-5 w-5 text-blue-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-50 border-green-200';
      case 'failed': return 'bg-red-50 border-red-200';
      case 'cancelled': return 'bg-gray-50 border-gray-200';
      default: return 'bg-blue-50 border-blue-200';
    }
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors"
        title="Notifications"
      >
        <BellIcon className="h-5 w-5" />
        
        {/* Badge for active/unread */}
        {(activeCount > 0 || hasUnread) && (
          <span className={`absolute -top-1 -right-1 h-4 w-4 rounded-full flex items-center justify-center text-xs text-white ${
            activeCount > 0 ? 'bg-blue-500 animate-pulse' : 'bg-red-500'
          }`}>
            {activeCount > 0 ? activeCount : '!'}
          </span>
        )}
      </motion.button>

      {/* Dropdown Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-1rem)] bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden"
          >
            {/* Header */}
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
              {notifications.length > 0 && (
                <button
                  onClick={onClearAll}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear all
                </button>
              )}
            </div>

            {/* Notifications List */}
            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-gray-500">
                  <BellIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No notifications</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`px-4 py-3 ${getStatusColor(notification.status)} border-l-4`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1 min-w-0">
                          {getStatusIcon(notification)}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {notification.title}
                            </p>
                            <p className="text-xs text-gray-600 mt-0.5">
                              {notification.message}
                            </p>
                            
                            {/* Progress bar for active downloads */}
                            {(notification.status === 'pending' || notification.status === 'processing') && 
                             notification.progress !== undefined && (
                              <div className="mt-2">
                                <div className="flex justify-between text-xs text-gray-500 mb-1">
                                  <span>{notification.status === 'pending' ? 'Preparing...' : 'Processing...'}</span>
                                  <span>{notification.progress}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                  <div 
                                    className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                                    style={{ width: `${notification.progress}%` }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* ZIP download action for completed download notifications */}
                            {notification.status === 'completed' && notification.type === 'download' && (
                              <div className="mt-2">
                                {notification.urlExpiresAt && isExpired(notification.urlExpiresAt) ? (
                                  <button
                                    onClick={notification.onRegenerate}
                                    className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-3 py-1 rounded-md text-sm"
                                  >
                                    Regenerate Link
                                  </button>
                                ) : notification.downloadUrl ? (
                                  <a
                                    href={notification.downloadUrl}
                                    download
                                    className="inline-block bg-gray-100 text-gray-700 hover:bg-gray-200 px-3 py-1 rounded-md text-sm"
                                  >
                                    Download ZIP
                                  </a>
                                ) : null}
                              </div>
                            )}
                            
                            <p className="text-xs text-gray-400 mt-1">
                              {formatTime(notification.timestamp)}
                            </p>
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center space-x-1 ml-2">
                          {notification.onCancel && 
                           (notification.status === 'pending' || notification.status === 'processing') && (
                            <button
                              onClick={notification.onCancel}
                              className="text-xs px-2 py-1 text-red-600 hover:bg-red-100 rounded"
                            >
                              Cancel
                            </button>
                          )}
                          {notification.canDismiss && (
                            <button
                              onClick={() => onDismiss(notification.id)}
                              className="p-1 text-gray-400 hover:text-gray-600"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
